


import { Component, Vue, Watch } from "vue-property-decorator";

import dayjs from "dayjs";

import { Coupons } from "App";

import {
    Coupon,
    CouponUsage,
    Customer,
    CustomNotification,
    Field,
    Plan,
    Price
} from "App/Models";

import { Dictionary } from "App/Contracts";

import { EventBus, $t } from "App/IOC";

import { RecordActions, RecordAction } from "Web/Contracts";

import CouponForm from "./Form.vue";

import Record from "Web/Components/Record.vue";
import Accordion from "Web/Components/Accordion.vue";

@Component({
    components: {
        Accordion,
        CouponForm,
        Record
    }
})
export default class SingleCouponView extends Vue {
    private record: Coupon = {
        Id: -1,
        Name: "",
        Plans: [],
        GlobalPrices: [],
        Customers: []
    };

    private fields: Dictionary<Field> = {};

    private generalConditions: string = "";
    private showScenarios: boolean = false;
    private editing: boolean = false;
    private loading: boolean = true;

    /* Customers who redeemed this coupon, shown in the right-hand panel. */
    private usages: CouponUsage[] = [];
    private usagesLoading: boolean = true;

    /* "Stopper le bon maintenant" is only offered while the coupon is still live. Stopping flags
       the coupon inactive, so once stopped the action is no longer shown. */
    private get isStopped(): boolean {
        return this.record.Active === false;
    }

    /* Audit line shown centered in the header once the coupon has been manually stopped: when and
       by whom. StoppedAt is a CustomDateTime; ICustomDate.Format() does not localize the month name
       (it only applies a locale when built with an i18n, which the deserializer doesn't), so we go
       through dayjs with the active app locale to get the month in French and keep the time. Empty
       while the coupon was never stopped by hand (older stopped coupons may also lack it). */
    private get stoppedNotice(): string {
        if( !this.isStopped || this.record.StoppedAt === undefined )
            return "";

        const date = dayjs( this.record.StoppedAt.ToDate() )
            .locale( this.$i18n.locale )
            .format( "D MMMM YYYY HH:mm" );

        return this.$t( "COUPONS_stoppedNotice", {
            date,
            user: this.record.StoppedBy || ""
        } ).toString();
    }

    private get actions(): RecordActions<Coupon> {
        const group: RecordAction<Coupon>[] = [
            {
                Id: "Customers",
                Title: $t( "COUPONS_actionCustomers" ),
                Icon: "people"
            }
        ];

        /* Deletion is only offered while the coupon has never been redeemed: once it has at least
           one use it carries history that must be kept, so the action is hidden. */
        if( this.record.Uses === undefined || this.record.Uses === 0 )
            group.unshift({
                Id: "Delete",
                Title: $t( "GLOBAL_actionDelete" ),
                Icon: "delete_forever"
            });

        if( !this.isStopped )
            group.unshift({
                Id: "Stop",
                Title: $t( "COUPONS_actionStop" ),
                Icon: "block"
            });

        return [ group ];
    }

    private async created() {
        await this.setup();
        await this.generateConditions();
    }

    private async setup() {
        this.fields = await Coupons.Fields.Get();

        /* TODO: Handle invalid ID format. */
        await this.getCoupon( true );

        this.loadUsages();

        if( this.record.Name === undefined )
            return;

        document.title = this.record.Name;
    }

    /* Load the customers who redeemed this coupon for the right-hand panel. Non-blocking: the form
       renders immediately and the panel fills in (or shows its empty state) once resolved. A
       failure (e.g. the endpoint not yet deployed -> 404) must NOT pop a blocking error toast for
       this secondary panel; we log it and leave the panel on its empty state. */
    private async loadUsages(): Promise<void> {
        this.usagesLoading = true;

        const [ usages, error ] = await Coupons.Usage( Number( this.$route.params.id ) );

        this.usagesLoading = false;

        if( error !== undefined ) {
            console.warn( "Failed to load coupon usage:", error.Message );
            return;
        }

        this.usages = usages;
    }

    /* Format a usage row's first-subscription date in the active locale (French month name). */
    private usageDate( usage: CouponUsage ): string {
        if( usage.FirstSubscriptionDate === undefined )
            return "—";

        return dayjs( usage.FirstSubscriptionDate.ToDate() )
            .locale( this.$i18n.locale )
            .format( "D MMMM YYYY" );
    }

    /* Coupon end date for this customer = first-subscription date + the coupon duration in months.
       Empty when the start date is unknown or the coupon has no fixed duration (permanent). */
    private usageEndDate( usage: CouponUsage ): string {
        const duration = this.record.Duration;

        if( usage.FirstSubscriptionDate === undefined || duration === undefined || duration <= 0 )
            return "—";

        return dayjs( usage.FirstSubscriptionDate.ToDate() )
            .add( duration, "month" )
            .locale( this.$i18n.locale )
            .format( "D MMMM YYYY" );
    }

    /* Reset data on Product change*/
    private resetData() {
        this.fields = {};
        this.usages = [];
        this.usagesLoading = true;
        this.record = {
            Id: -1,
            Name: "",
            Plans: [],
            GlobalPrices: [],
            Customers: []
        };
    }

    private async generateConditions(): Promise<void> {
        const [ condition, error ] = await Coupons.GenerateGeneralConditions( this.record );

        if( error !== undefined || condition === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.generalConditions = condition;
    }

    /* Refresh data if navigated to a different Product
        diractly from the current page. */
    @Watch( "$route" )
    private onRouteParametersChange( from: any, to: any ) {
        if( to.name !== "coupon" || from.params.id === to.params.id )
            return;
        this.resetData();
        this.setup();
    }

    private async getCoupon( silent: boolean = false ) {
        const [ record, error ] = await Coupons.Get( Number( this.$route.params.id ) );

        if( error !== undefined || record === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.prefixWithProductName( this.record.Plans );
        this.prefixWithProductName( this.record.GlobalPrices );
        this.prefixCustomerWithEmail( this.record.Customers );

        this.record = record as Coupon;

        if( !silent )
            this.showUpdateNotification();
    }

    private prefixWithProductName( items: Plan[] | Price[] ): void {
        /* Format plan names with their respective product as prefix. */
        for( const item of items )
            item.Name = item.Product.Name + " - " + item.Name;
    }


    private prefixCustomerWithEmail( customers: Customer[] ): void {
        for( const customer of customers )
            customer.Name = customer.Email + " - " + customer.Name;
    }

    private showUpdateNotification() {
        EventBus.Publish( "Notification", CustomNotification.Success(
            this.$t( "GLOBAL_COUPON" ).toString(), this.$t( "COUPONS_updated" ).toString()
        ));
    }

    private underConstruction() {
        EventBus.Publish( "UnderConstruction" );
    }

    private update( coupon: Coupon, silent: boolean = false ) {
        this.record = coupon;

        if( !silent )
            this.showUpdateNotification();
    }

    private execute( action: RecordAction ): void {
        switch( action.Id ) {
            case "Delete":
                this.delete( this.record as Coupon );
                break;

            case "Customers":
                this.$router.push( `/customers?coupon=${this.record.Id}` );
                break;

            case "Stop":
                this.stop( this.record as Coupon );
                break;

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }

    /* Stop the coupon now: block any future redemption and end the discount for every subscription
       currently using it (reverted to full price on its next invoice). The change is irreversible
       from here, so it goes through a confirmation. */
    private stop( coupon: Coupon ): void {
        EventBus.Publish( "Confirmation", {
            Props: {
                Message: this.$t( "COUPONS_stopConfirm" ),
                Actions: [{
                    Id: "stop",
                    Name: this.$t( "COUPONS_actionStop" ),
                    Active: true
                }]
            },

            Events: {
                Action: async () => {
                    const error = await Coupons.Stop( coupon.Id );

                    if( error !== undefined ) {
                        EventBus.Publish( "Error", error );
                        return;
                    }

                    EventBus.Publish( "Notification", CustomNotification.Success(
                        this.$t( "GLOBAL_COUPON" ).toString(),
                        this.$t( "COUPONS_stopped" ).toString()
                    ));

                    await this.getCoupon( true );
                }
            }
        })
    }

    private async delete( coupon: Coupon ) {
        EventBus.Publish( "Confirmation", {
            Props: {
                Message: this.$tc( "COUPONS_deleteConfirm", 1 ),
                Actions: [{
                    Id: "delete",
                    Name: this.$t( "GLOBAL_actionDelete" ),
                    Active: true
                }]
            },

            Events: {
                Action: async () => {
                    const error = await Coupons.Delete( coupon );

                    if( error !== undefined )
                        EventBus.Publish( "Error", error );

                    this.$router.replace( "/coupons" );
                }
            }
        })
    }

    @Watch( "record" )
    private onRecordChange( record?: Coupon ) {
        if( record === undefined )
            return;

        setTimeout( () => this.loading = record.Name === undefined, 200 );
    }

    private async save( record: Coupon ): Promise<void> {
        if( this.record === undefined || record === undefined )
            return;

        const [ updatedRecord, error ] = await Coupons.Update( this.record, record );

        if( error !== undefined || updatedRecord === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.record = updatedRecord;

        this.prefixWithProductName( this.record.Plans );
        this.prefixWithProductName( this.record.GlobalPrices );
        this.prefixCustomerWithEmail( this.record.Customers );

        this.showUpdateNotification();
    }
}
